export type DestinationType =
  | 'app_register'
  | 'app_login'
  | 'app_properties'
  | 'contact'
  | 'newsletter'
  | 'calendly'
  | 'whitepaper'
  | 'blog'
  | 'faq'
  | 'social'
  | 'ask_ai'
  | 'internal'
  | 'external'
  | 'unknown'

export type LeadType = 'contact_form' | 'newsletter' | 'meeting' | 'whitepaper'

type ConsentValue = 'granted' | 'denied'

type DataLayerValue =
  | string
  | number
  | boolean
  | null
  | undefined
  | Record<string, unknown>
  | Array<unknown>

type DataLayerEvent = {
  event: string
  [key: string]: DataLayerValue
}

declare global {
  interface Window {
    dataLayer?: unknown[]
    gtag?: (...args: unknown[]) => void
  }
}

const ensureDataLayer = () => {
  if (typeof window === 'undefined') return []

  window.dataLayer = window.dataLayer || []
  return window.dataLayer
}

const getPageSection = (path: string) => {
  const cleanPath = path.split('?')[0]

  if (cleanPath.includes('/blog/')) return 'blog'
  if (cleanPath.includes('/contact/')) return 'contact'
  if (cleanPath.includes('/newsletter/')) return 'newsletter'
  if (cleanPath.includes('/team/')) return 'team'
  if (cleanPath.includes('/privacy-policy/')) return 'legal'
  if (cleanPath.includes('/legal-notices/')) return 'legal'
  if (cleanPath.includes('/terms-of-use/')) return 'legal'
  if (cleanPath === '/' || cleanPath === '/fr/' || cleanPath === '/en/') {
    return 'home'
  }

  return 'other'
}

const getDomain = (url: string) => {
  try {
    return new URL(url, window.location.origin).hostname
  } catch {
    return ''
  }
}

const pushEvent = (event: DataLayerEvent) => {
  const dataLayer = ensureDataLayer()
  dataLayer.push(event)
}

const updateGoogleConsent = ({
  analyticsStorage,
  adStorage,
  adUserData,
  adPersonalization,
}: {
  analyticsStorage: ConsentValue
  adStorage: ConsentValue
  adUserData: ConsentValue
  adPersonalization: ConsentValue
}) => {
  if (typeof window === 'undefined') return

  window.gtag =
    window.gtag ||
    function gtag() {
      ensureDataLayer().push(arguments)
    }

  window.gtag('consent', 'update', {
    analytics_storage: analyticsStorage,
    ad_storage: adStorage,
    ad_user_data: adUserData,
    ad_personalization: adPersonalization,
  })
}

const GtmService = {
  trackEvent(event: DataLayerEvent) {
    pushEvent(event)
  },

  pageView({
    pagePath,
    pageLocation,
    pageTitle,
    language,
  }: {
    pagePath: string
    pageLocation: string
    pageTitle: string
    language: string
  }) {
    pushEvent({
      event: 'page_view',
      page_path: pagePath,
      page_location: pageLocation,
      page_title: pageTitle,
      language,
      site_section: getPageSection(pagePath),
    })
  },

  consentUpdate(choice: ConsentValue) {
    const analyticsStorage = choice
    const adStorage = choice
    const adUserData = choice
    const adPersonalization = choice

    updateGoogleConsent({
      analyticsStorage,
      adStorage,
      adUserData,
      adPersonalization,
    })

    pushEvent({
      event: 'consent_update',
      analytics_storage: analyticsStorage,
      ad_storage: adStorage,
      ad_user_data: adUserData,
      ad_personalization: adPersonalization,
    })
  },

  ctaClicked({
    ctaId,
    ctaLabel,
    ctaLocation,
    destinationUrl,
    destinationType = 'unknown',
    language,
  }: {
    ctaId: string
    ctaLabel: string
    ctaLocation: string
    destinationUrl: string
    destinationType?: DestinationType
    language?: string
  }) {
    pushEvent({
      event: 'cta_click',
      cta_id: ctaId,
      cta_label: ctaLabel,
      cta_location: ctaLocation,
      destination_url: destinationUrl,
      destination_type: destinationType,
      language,
    })

    if (destinationType === 'calendly') {
      this.scheduleMeetingIntent({
        ctaLocation,
        destinationUrl,
      })
    }
  },

  generateLead({
    leadType,
    leadSource,
    ctaLocation,
    emailDomain,
    subject,
  }: {
    leadType: LeadType
    leadSource: string
    ctaLocation?: string
    emailDomain?: string
    subject?: string
  }) {
    pushEvent({
      event: 'generate_lead',
      lead_type: leadType,
      lead_source: leadSource,
      cta_location: ctaLocation,
      email_domain: emailDomain,
      subject,
    })
  },

  scheduleMeetingIntent({
    ctaLocation,
    destinationUrl,
    selectedDate,
    selectedTime,
  }: {
    ctaLocation: string
    destinationUrl?: string
    selectedDate?: string
    selectedTime?: string
  }) {
    pushEvent({
      event: 'schedule_meeting_intent',
      cta_location: ctaLocation,
      destination_url: destinationUrl,
      selected_date: selectedDate,
      selected_time: selectedTime,
    })
  },

  outboundClick({
    linkUrl,
    linkLabel,
    linkLocation,
  }: {
    linkUrl: string
    linkLabel: string
    linkLocation: string
  }) {
    pushEvent({
      event: 'outbound_click',
      link_domain: getDomain(linkUrl),
      link_url: linkUrl,
      link_label: linkLabel,
      link_location: linkLocation,
    })
  },

  contentEngagement({
    contentType,
    contentId,
    contentTitle,
    interactionType,
  }: {
    contentType: string
    contentId?: string
    contentTitle?: string
    interactionType: string
  }) {
    pushEvent({
      event: 'content_engagement',
      content_type: contentType,
      content_id: contentId,
      content_title: contentTitle,
      interaction_type: interactionType,
    })
  },

  newsletterSubmitted({
    emailDomain,
    clickSource,
  }: {
    emailDomain: string
    clickSource: 'quiz' | 'footer' | 'newsletter-subscribe-page'
  }) {
    this.generateLead({
      leadType: 'newsletter',
      leadSource: clickSource,
      ctaLocation: clickSource,
      emailDomain,
    })
  },
}

export default GtmService
